import Vue from 'vue'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_moment_2c93f215 from 'nuxt_plugin_moment_2c93f215' // Source: ./moment.js (mode: 'all')
import nuxt_plugin_googletagmanager_3c0f2b22 from 'nuxt_plugin_googletagmanager_3c0f2b22' // Source: ./google-tag-manager.js (mode: 'client')
import nuxt_plugin_sentryclient_b8d251c2 from 'nuxt_plugin_sentryclient_b8d251c2' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_axios_117b1be3 from 'nuxt_plugin_axios_117b1be3' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_nuxtplugin2a20e24a_48481b04 from 'nuxt_plugin_nuxtplugin2a20e24a_48481b04' // Source: ./nuxt.plugin.2a20e24a.js (mode: 'all')
import nuxt_plugin_firebase_27cb801c from 'nuxt_plugin_firebase_27cb801c' // Source: ../plugins/firebase.js (mode: 'client')
import nuxt_plugin_pushnotification_416b1726 from 'nuxt_plugin_pushnotification_416b1726' // Source: ../plugins/push-notification.js (mode: 'client')
import nuxt_plugin_logger_3637d6f3 from 'nuxt_plugin_logger_3637d6f3' // Source: ../plugins/logger.js (mode: 'client')
import nuxt_plugin_axios_397e53b5 from 'nuxt_plugin_axios_397e53b5' // Source: ../plugins/axios.js (mode: 'all')
import nuxt_plugin_helpers_4ee54374 from 'nuxt_plugin_helpers_4ee54374' // Source: ../plugins/helpers.js (mode: 'all')
import nuxt_plugin_filters_14a52510 from 'nuxt_plugin_filters_14a52510' // Source: ../plugins/filters.js (mode: 'all')
import nuxt_plugin_mailer_e2fef942 from 'nuxt_plugin_mailer_e2fef942' // Source: ../plugins/mailer.js (mode: 'all')
import nuxt_plugin_plugin_942f1a42 from 'nuxt_plugin_plugin_942f1a42' // Source: ./auth/plugin.js (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp (ssrContext) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  const registerModule = store.registerModule
  store.registerModule = (path, rawModule, options) => registerModule.call(store, path, rawModule, Object.assign({ preserveState: process.client }, options))

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"PAPP"}],"link":[],"style":[],"script":[{"src":"\u002F\u002Fwww.googletagmanager.com\u002Fgtm.js?id=GTM-NQMB9QL&l=dataLayer","async":true}]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  const inject = function (key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Plugin execution

  if (typeof nuxt_plugin_moment_2c93f215 === 'function') {
    await nuxt_plugin_moment_2c93f215(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_googletagmanager_3c0f2b22 === 'function') {
    await nuxt_plugin_googletagmanager_3c0f2b22(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_b8d251c2 === 'function') {
    await nuxt_plugin_sentryclient_b8d251c2(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_117b1be3 === 'function') {
    await nuxt_plugin_axios_117b1be3(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxtplugin2a20e24a_48481b04 === 'function') {
    await nuxt_plugin_nuxtplugin2a20e24a_48481b04(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_firebase_27cb801c === 'function') {
    await nuxt_plugin_firebase_27cb801c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_pushnotification_416b1726 === 'function') {
    await nuxt_plugin_pushnotification_416b1726(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_logger_3637d6f3 === 'function') {
    await nuxt_plugin_logger_3637d6f3(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_397e53b5 === 'function') {
    await nuxt_plugin_axios_397e53b5(app.context, inject)
  }

  if (typeof nuxt_plugin_helpers_4ee54374 === 'function') {
    await nuxt_plugin_helpers_4ee54374(app.context, inject)
  }

  if (typeof nuxt_plugin_filters_14a52510 === 'function') {
    await nuxt_plugin_filters_14a52510(app.context, inject)
  }

  if (typeof nuxt_plugin_mailer_e2fef942 === 'function') {
    await nuxt_plugin_mailer_e2fef942(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_942f1a42 === 'function') {
    await nuxt_plugin_plugin_942f1a42(app.context, inject)
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, () => {
        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from, next) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
